import React, { useMemo } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { DeleteOutline, Edit } from '@mui/icons-material';
import { isNotEmpty } from 'utils/isNotEmpty';
import { calculatePercentage } from 'utils/calculatePercentage';

const markLabels: Record<number, string> = {
  0: 'Unimportant',
  0.5: 'Less important',
  1: 'Neutral',
  1.5: 'Important',
  2: 'Very important'
};

type Props = {
  content: string;
  weight: number;
  onEdit: () => void;
  onDelete: () => void;
  candidateBatchCandidateCount?: number | null;
  requirementMetCandidatesCount?: number | null;
};

export const RequirementItemView: React.FC<Props> = ({
  content,
  weight,
  onEdit,
  onDelete,
  candidateBatchCandidateCount,
  requirementMetCandidatesCount
}) => {
  const heading = useMemo(() => {
    if (
      isNotEmpty(candidateBatchCandidateCount) &&
      candidateBatchCandidateCount > 0 &&
      isNotEmpty(requirementMetCandidatesCount)
    ) {
      const percentage = calculatePercentage(
        requirementMetCandidatesCount,
        candidateBatchCandidateCount
      );

      if (percentage === 0) {
        return `No applicants met this ${markLabels[
          weight
        ].toLowerCase()} requirement`;
      }

      if (percentage === 100) {
        return `All applicants met this ${markLabels[
          weight
        ].toLowerCase()} requirement`;
      }

      return `${percentage}% (${requirementMetCandidatesCount}) applicants met this ${markLabels[
        weight
      ].toLowerCase()} requirement`;
    }
  }, [candidateBatchCandidateCount, requirementMetCandidatesCount, weight]);

  return (
    <>
      {heading && <Typography>{heading}</Typography>}
      <Stack direction="row" alignItems="start">
        <Typography variant="summary" flex={1}>
          {content}
        </Typography>
        <Stack direction="row" gap={2}>
          <IconButton size="small" onClick={onEdit} color="primary">
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteOutline />
          </IconButton>
        </Stack>
      </Stack>
    </>
  );
};
